import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { DEFAULT_MENS_ROTATION, DEFAULT_WOMENS_ROTATION, ensureCompetitionRotations } from '../constants/competitionConstants';
import { supabase } from '../services/supabase';

// Generic merge helper: upsert by id and preserve selected fields when incoming data omits them
const mergeEntitiesPreserveFields = (incoming = [], existing = [], preserveFields = []) => {
  const existingMap = new Map(existing.map(item => [String(item.id), item]));
  const merged = [];

  incoming.forEach((item, idx) => {
    const key = item?.id != null ? String(item.id) : `__idx_${idx}`;
    const existingItem = existingMap.get(key);
    if (existingItem) {
      const combined = { ...existingItem, ...item };
      preserveFields.forEach(field => {
        if (item[field] === undefined || item[field] === null || item[field] === '') {
          combined[field] = existingItem[field];
        }
      });
      merged.push(combined);
      existingMap.delete(key);
    } else {
      merged.push(item);
    }
  });

  // Keep any existing items not present in the incoming list
  existingMap.forEach(item => merged.push(item));

  return merged;
};

// Merge incoming athletes with existing ones while preserving assignments/numbering when new data is missing them
const mergeAthletesPreserveAssignments = (incoming = [], existing = []) => {
  const existingMap = new Map(existing.map(a => [String(a.id), a]));
  const merged = [];

  incoming.forEach((athlete, idx) => {
    const existingAthlete = existingMap.get(String(athlete.id));
    if (existingAthlete) {
      const combined = { ...existingAthlete, ...athlete };
      const preserveIfMissing = (field) => {
        if (athlete[field] === undefined || athlete[field] === null || athlete[field] === '') {
          combined[field] = existingAthlete[field];
        }
      };

      ['session', 'flight', 'squad', 'order', 'number', 'notes', 'ageGroup', 'programAgeGroupAge'].forEach(preserveIfMissing);
      combined.scratched = athlete.scratched !== undefined ? athlete.scratched : existingAthlete.scratched || false;

      merged.push(combined);
      existingMap.delete(String(athlete.id));
    } else {
      merged.push(athlete);
    }
  });

  // Keep any existing athletes not present in the incoming list
  existingMap.forEach((athlete) => merged.push(athlete));

  return merged;
};

export const fetchCompetition = createAsyncThunk(
  'competition/fetchCompetition',
  async (id) => {
    // Get the base competition data first
    const { data: competition, error: competitionError } = await supabase
      .from('competitions')
      .select('*')
      .eq('id', id)
      .neq('status', 'deleted')
      .single();

    if (competitionError) throw competitionError;

    // Then try to get the latest version
    const { data: latestVersion, error: versionError } = await supabase
      .from('competition_configs')
      .select('content')
      .eq('competition_id', id)
      .order('revision', { ascending: false })
      .limit(1)
      .single();

    if (versionError && versionError.code !== 'PGRST116') throw versionError;

    if (latestVersion?.content) {
      // Merge base competition data with latest version content
      return {
        ...latestVersion.content,
        competition: {
          ...latestVersion.content.competition,
          // Only overwrite specific fields from base competition table
          id: competition.id,
          status: competition.status
        }
      };
    }

    // If no version exists, return base structure with competition data
    return {
      competition,
      statistics: {},
      sessions: [],
      teams: [],
      gymnasts: [],
      levels: [],
      coaches: [],
      judges: [],
      ageGroups: [],
      events: [],
      rotations: [DEFAULT_MENS_ROTATION, DEFAULT_WOMENS_ROTATION]
    };
  }
);

export const addCompetition = createAsyncThunk(
  'competition/addCompetition',
  async (payload) => {
    if (payload.competition) {
      const withRotations = ensureCompetitionRotations(payload);
      
      // First create the competition entry
      const { data: competition, error: competitionError } = await supabase
        .from('competitions')
        .insert([{ 
          id: withRotations.competition.id,
          status: withRotations.competition.status || 'draft'
        }])
        .single();

      if (competitionError) throw competitionError;

      // Then create the competition config
      const { data: config, error: configError } = await supabase
        .from('competition_configs')
        .insert([{
          competition_id: withRotations.competition.id,
          content: withRotations,
          revision: 1
        }]);

      if (configError) throw configError;

      // Grant owner access to current user
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (user) {
        const { error: accessError } = await supabase
          .from('competition_access')
          .insert([{
            user_id: user.id,
            competition_id: withRotations.competition.id,
            role: 'owner'
          }]);

        if (accessError) throw accessError;
      }

      return withRotations;
    }
    
    // Handle non-structured competition creation
    return {
      competition: {
        ...payload,
        id: Date.now().toString(),
        addedAt: new Date().toISOString(),
        startDate: payload.startDate || null,
        endDate: payload.endDate || null,
        location: payload.location || {
          lat: null,
          lng: null
        }
      },
      statistics: {
        totalTeams: 0,
        totalGymnasts: 0,
        totalScratchedGymnasts: 0,
        totalJudges: 0,
        totalSessions: 0,
        gymnastsByLevel: {},
        gymnastsByAgeGroup: {}
      },
      sessions: [],
      teams: [],
      gymnasts: [],
      rotations: [DEFAULT_MENS_ROTATION, DEFAULT_WOMENS_ROTATION]
    };
  }
);

export const duplicateCompetition = createAsyncThunk(
  'competition/duplicateCompetition',
  async (sourceCompetition) => {
    // Generate new ID for the duplicate
    const newId = Math.random().toString(36).substr(2, 12);
    const timestamp = new Date().toISOString();

    // Create a deep copy of the competition data
    const duplicatedData = {
      ...sourceCompetition,
      competition: {
        ...sourceCompetition.competition,
        id: newId,
        name: `${sourceCompetition.competition.name} (Copy)`,
        addedAt: timestamp,
        status: 'draft', // Reset to draft status
      },
      // Preserve all other data (teams, gymnasts, sessions, etc.)
      statistics: { ...sourceCompetition.statistics },
      sessions: sourceCompetition.sessions ? [...sourceCompetition.sessions] : [],
      teams: sourceCompetition.teams ? [...sourceCompetition.teams] : [],
      gymnasts: sourceCompetition.gymnasts ? [...sourceCompetition.gymnasts] : [],
      levels: sourceCompetition.levels ? [...sourceCompetition.levels] : [],
      coaches: sourceCompetition.coaches ? [...sourceCompetition.coaches] : [],
      judges: sourceCompetition.judges ? [...sourceCompetition.judges] : [],
      ageGroups: sourceCompetition.ageGroups ? [...sourceCompetition.ageGroups] : [],
      events: sourceCompetition.events ? [...sourceCompetition.events] : [],
      rotations: sourceCompetition.rotations ? [...sourceCompetition.rotations] : [DEFAULT_MENS_ROTATION, DEFAULT_WOMENS_ROTATION]
    };

    const withRotations = ensureCompetitionRotations(duplicatedData);

    // First create the competition entry
    const { data: competition, error: competitionError } = await supabase
      .from('competitions')
      .insert([{
        id: withRotations.competition.id,
        status: withRotations.competition.status
      }])
      .single();

    if (competitionError) throw competitionError;

    // Then create the competition config
    const { data: config, error: configError } = await supabase
      .from('competition_configs')
      .insert([{
        competition_id: withRotations.competition.id,
        content: withRotations,
        revision: 1
      }]);

    if (configError) throw configError;

    // Grant owner access to current user
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    if (user) {
      const { error: accessError } = await supabase
        .from('competition_access')
        .insert([{
          user_id: user.id,
          competition_id: withRotations.competition.id,
          role: 'owner'
        }]);

      if (accessError) throw accessError;
    }

    return withRotations;
  }
);

export const deleteCompetitionFromDB = createAsyncThunk(
  'competition/deleteCompetitionFromDB',
  async (competitionId) => {
    // Mark competition as deleted instead of actually deleting
    const { error: competitionError } = await supabase
      .from('competitions')
      .update({ status: 'deleted' })
      .eq('id', competitionId);

    if (competitionError) throw competitionError;

    return competitionId;
  }
);

const competitionSlice = createSlice({
  name: 'competition',
  initialState: {
    competitions: [],
    currentCompetition: null,
    loading: false,
    error: null,
    userRole: null,
    accessRights: {},  // Map of competition IDs to user roles
  },
  reducers: {
    addCompetitionStart: (state) => {
      state.loading = true;
      state.error = null;
    },
    addCompetitionSuccess: (state, action) => {
      state.loading = false;
      state.competitions.push(action.payload);
      state.currentCompetition = action.payload;
      state.accessRights[action.payload.competition.id] = 'owner';
    },
    addCompetitionError: (state, action) => {
      state.loading = false;
      state.error = action.payload;

      // Otherwise, structure the data
      const competitionWithTimestamp = {
        competition: {
          ...action.payload,
          id: Date.now().toString(),
          addedAt: new Date().toISOString(),
          startDate: action.payload.startDate || null,
          endDate: action.payload.endDate || null,
          location: action.payload.location || {
            lat: null,
            lng: null
          }
        },
        statistics: {
          totalTeams: 0,
          totalGymnasts: 0,
          totalScratchedGymnasts: 0,
          totalJudges: 0,
          totalSessions: 0,
          gymnastsByLevel: {},
          gymnastsByAgeGroup: {}
        },
        sessions: [],
        teams: [],
        gymnasts: [],
        rotations: [DEFAULT_MENS_ROTATION, DEFAULT_WOMENS_ROTATION]
      };
      state.competitions = [...state.competitions, competitionWithTimestamp];
      state.currentCompetition = competitionWithTimestamp;
    },
    setCompetitions: (state, action) => {
      state.competitions = action.payload;
    },
    setCurrentCompetition: (state, action) => {
      state.currentCompetition = action.payload;
    },
    setLoading: (state, action) => {
      state.loading = action.payload;
    },
    setError: (state, action) => {
      state.error = action.payload;
    },
    deleteCompetition: (state, action) => {
      state.competitions = state.competitions.filter(comp => 
        comp.competition.id !== action.payload
      );
    },
    updateCompetition: (state, action) => {
      let index = -1;

      // console.log(state.competitions)

      if (action.payload.type) {
        // For child object updates, find the current competition
        index = state.competitions.findIndex(
          comp => comp.competition.id === state.currentCompetition.competition.id
        );
      } else {
        // For full competition updates
        index = state.competitions.findIndex(
          comp => comp.competition.name === action.payload.competition.name
        );
      }
      if (index !== -1) {
        if (action.payload.type) {
          // Handle child object updates
          switch (action.payload.type) {
            case 'gymnasts':
              // Create a complete copy of the current competition state
              const updatedCompetitionWithGymnasts = {
                ...state.competitions[index],
                gymnasts: action.payload.data
              };
              
              // Update both the competitions array and currentCompetition
              state.competitions[index] = updatedCompetitionWithGymnasts;
              state.currentCompetition = updatedCompetitionWithGymnasts;
              
              // Set toast if requested
              if (action.payload.showToast) {
                state.toast = {
                  message: action.payload.toastMessage || 'Gymnasts updated successfully',
                  severity: 'success',
                  undoAction: action.payload.undoAction || null
                };
              }
              break;

            case 'athletes':
              // Merge athletes while preserving assignments/numbering when incoming data lacks them
              const mergedAthletes = mergeAthletesPreserveAssignments(
                action.payload.data.gymnasts.map(gymnast => ({
                  ...gymnast,
                  number: gymnast.number || null // Ensure number is null if not set
                })),
                state.competitions[index].gymnasts || []
              );

              const mergedGymnastsByLevel = mergedAthletes.reduce((acc, a) => {
                if (a.level) acc[a.level] = (acc[a.level] || 0) + 1;
                return acc;
              }, {});
              const mergedGymnastsByAgeGroup = mergedAthletes.reduce((acc, a) => {
                const ageGroup = a.ageGroup || 'Unknown';
                acc[ageGroup] = (acc[ageGroup] || 0) + 1;
                return acc;
              }, {});

              const updatedCompetitionWithAthletes = {
                ...state.competitions[index],
                gymnasts: mergedAthletes,
                statistics: {
                  ...state.competitions[index].statistics,
                  ...(action.payload.data.statistics || {}),
                  totalGymnasts: mergedAthletes.length,
                  totalScratches: mergedAthletes.filter(a => a.scratched).length,
                  gymnastsByLevel: mergedGymnastsByLevel,
                  gymnastsByAgeGroup: mergedGymnastsByAgeGroup
                }
              };

              if (action.payload.data.metadata?.usagLastModified) {
                updatedCompetitionWithAthletes.metadata = {
                  ...updatedCompetitionWithAthletes.metadata,
                  usagLastModified: action.payload.data.metadata.usagLastModified
                };
              }

              // Update session statistics if provided
              if (action.payload.data.sessionStats) {
                // console.log('Updating session stats in reducer:', action.payload.data.sessionStats);
                updatedCompetitionWithAthletes.sessions = updatedCompetitionWithAthletes.sessions.map(session => {
                  const sessionStats = action.payload.data.sessionStats[session.sessionNumber] || {
                    totalGymnasts: 0,
                    gymnastsByLevel: {},
                    gymnastsByAgeGroup: {}
                  };
                  // console.log(`Stats for session ${session.sessionNumber}:`, sessionStats);
                  return {
                    ...session,
                    gymnastCount: sessionStats.totalGymnasts || 0,
                    statistics: sessionStats
                  };
                });
              }

              state.competitions[index] = updatedCompetitionWithAthletes;
              state.currentCompetition = updatedCompetitionWithAthletes;
              state.importStats = {
                type: 'athletes',
                count: action.payload.data.gymnasts.length
              };
              break;
            case 'coaches':
              const mergedCoaches = mergeEntitiesPreserveFields(
                action.payload.data.coaches,
                state.competitions[index].coaches || [],
                ['email', 'phone', 'address', 'city', 'state', 'zip', 'role', 'proNumber', 'usagVerified', 'notes']
              );
              const updatedCompetitionWithCoaches = {
                ...state.competitions[index],
                coaches: mergedCoaches
              };
              if (action.payload.data.metadata?.usagLastModified) {
                updatedCompetitionWithCoaches.metadata = {
                  ...updatedCompetitionWithCoaches.metadata,
                  usagLastModified: action.payload.data.metadata.usagLastModified
                };
              }
              state.competitions[index] = updatedCompetitionWithCoaches;
              state.currentCompetition = updatedCompetitionWithCoaches;
              break;
            case 'judges':
              // Handle USAG JUDGES import - only updates judges, preserves all other data
              const mergedJudges = mergeEntitiesPreserveFields(
                action.payload.data.judges || [],
                state.competitions[index].judges || [],
                ['email', 'phone', 'notes', 'assignments']
              );
              const updatedCompetitionWithJudges = {
                ...state.competitions[index],
                judges: mergedJudges,
                statistics: {
                  ...state.competitions[index].statistics,
                  totalJudges: mergedJudges.length
                }
              };
              if (action.payload.data.metadata?.usagLastModified) {
                updatedCompetitionWithJudges.metadata = {
                  ...updatedCompetitionWithJudges.metadata,
                  usagLastModified: action.payload.data.metadata.usagLastModified
                };
              }
              state.competitions[index] = updatedCompetitionWithJudges;
              state.currentCompetition = updatedCompetitionWithJudges;
              state.importStats = {
                type: 'judges',
                count: (action.payload.data.judges || []).length
              };
              break;
            case 'clubs':
              // Transform club data to match our team schema
              const transformedTeams = action.payload.data.teams.map(club => ({
                id: club.id || Math.floor(Math.random() * 1000000) + '',
                name: club.name,
                shortName: club.shortName,
                city: club.city || '',
                state: club.state || '',
                country: club.country || 'USA',
                region: club.region || '',
                address: club.address || '',
                contact: club.contact || '',
                phone: club.phone || '',
                email: club.email || '',
                website: club.website || '',
                memberNumber: club.memberNumber || '',
                status: club.status?.toLowerCase() === 'inactive' ? 'inactive' : 'active',
                type: 'club',
                coaches: [], // Will be populated when coaches are imported
                contacts: {
                  club: {
                    name: club.contacts.club.name || '',
                    email: club.contacts.club.email || '',
                    phone:  club.contacts.club.phone || ''
                  },
                  meet: {
                    name:  club.contacts.meet.name || '',
                    email: club.contacts.meet.email || '',
                    phone: club.contacts.meet.phone || ''
                  }
                }
              }));
              const mergedTeams = mergeEntitiesPreserveFields(
                transformedTeams,
                state.competitions[index].teams || [],
                ['contact', 'phone', 'email', 'website', 'coaches', 'address', 'city', 'state', 'region']
              );
              state.competitions[index].teams = mergedTeams;
              if (action.payload.data.metadata?.usagLastModified) {
                state.competitions[index].metadata = {
                  ...state.competitions[index].metadata,
                  usagLastModified: action.payload.data.metadata.usagLastModified
                };
              }
              
              const updatedCompetition = {
                ...state.competitions[index],
                statistics: {
                  ...state.competitions[index].statistics,
                  totalTeams: transformedTeams.length,
                }
              };
              state.competitions[index] = updatedCompetition;
              // Store count in state for notification
              state.importStats = {
                type: 'clubs',
                count: transformedTeams.length
              };
              break;
            case 'individuals':
              // Handle USAG INDIVIDUALS import - contains athletes, coaches, judges, and other staff
              const mergedIndividualsAthletes = mergeAthletesPreserveAssignments(
                (action.payload.data.gymnasts || []).map(gymnast => ({
                  ...gymnast,
                  number: gymnast.number || null
                })),
                state.competitions[index].gymnasts || []
              );

              const mergedIndividualsCoaches = action.payload.data.coaches && action.payload.data.coaches.length > 0
                ? mergeEntitiesPreserveFields(
                  action.payload.data.coaches,
                  state.competitions[index].coaches || [],
                  ['email', 'phone', 'address', 'city', 'state', 'zip', 'role', 'proNumber', 'usagVerified', 'notes']
                )
                : state.competitions[index].coaches;

              // Preserve totalTeams/totalClubs if incoming data has 0 but existing has values
              // (e.g., when importing a Judge file with empty reservations)
              const existingStats = state.competitions[index].statistics || {};
              const incomingStats = action.payload.data.statistics || {};
              const preserveTotalTeams = incomingStats.totalTeams === 0 && existingStats.totalTeams > 0
                ? existingStats.totalTeams
                : incomingStats.totalTeams;
              const preserveTotalClubs = incomingStats.totalClubs === 0 && existingStats.totalClubs > 0
                ? existingStats.totalClubs
                : incomingStats.totalClubs;

              const mergedIndividualsStats = {
                ...existingStats,
                ...incomingStats,
                // Preserve club counts when incoming data is empty
                totalTeams: preserveTotalTeams,
                totalClubs: preserveTotalClubs,
                totalGymnasts: mergedIndividualsAthletes.length,
                totalScratches: mergedIndividualsAthletes.filter(a => a.scratched).length,
                gymnastsByLevel: mergedIndividualsAthletes.reduce((acc, a) => {
                  if (a.level) acc[a.level] = (acc[a.level] || 0) + 1;
                  return acc;
                }, {}),
                gymnastsByAgeGroup: mergedIndividualsAthletes.reduce((acc, a) => {
                  const ageGroup = a.ageGroup || 'Unknown';
                  acc[ageGroup] = (acc[ageGroup] || 0) + 1;
                  return acc;
                }, {})
              };

              const updatedCompetitionWithIndividuals = {
                ...state.competitions[index],
                // Update gymnasts if provided, otherwise keep existing
                gymnasts: mergedIndividualsAthletes,
                // Update coaches if provided
                ...(mergedIndividualsCoaches ? { coaches: mergedIndividualsCoaches } : {}),
                // Update judges if provided
                ...(action.payload.data.judges && action.payload.data.judges.length > 0 && {
                  judges: action.payload.data.judges
                }),
                // Update teams if provided
                ...(action.payload.data.teams && action.payload.data.teams.length > 0 && {
                  teams: action.payload.data.teams
                }),
                // Update statistics
                statistics: mergedIndividualsStats
              };

              state.competitions[index] = updatedCompetitionWithIndividuals;
              state.currentCompetition = updatedCompetitionWithIndividuals;
              if (action.payload.data.metadata?.usagLastModified) {
                const metaValue = action.payload.data.metadata.usagLastModified;
                state.competitions[index].metadata = {
                  ...state.competitions[index].metadata,
                  usagLastModified: metaValue
                };
                state.currentCompetition.metadata = state.competitions[index].metadata;
              }
              
              // Track import stats for notification
              state.importStats = {
                type: 'individuals',
                count: {
                  gymnasts: action.payload.data.gymnasts?.length || 0,
                  coaches: action.payload.data.coaches?.length || 0,
                  judges: action.payload.data.judges?.length || 0,
                  other: action.payload.data.individuals?.length || 0
                }
              };
              break;
            case 'toast':
              // Just set the toast message
              if (action.payload.showToast && action.payload.data) {
                state.toast = {
                  message: action.payload.data.message || 'Update in progress...',
                  severity: action.payload.data.severity || 'info',
                  undoAction: action.payload.undoAction || null
                };
              }
              break;
            case 'reset': {
              // Direct reset of specified data types (no merging)
              const resetPayload = action.payload.data;
              const current = state.competitions[index];

              // Build the updated competition object
              const updated = {
                ...current,
                ...(resetPayload.gymnasts !== undefined && { gymnasts: resetPayload.gymnasts }),
                ...(resetPayload.coaches !== undefined && { coaches: resetPayload.coaches }),
                ...(resetPayload.teams !== undefined && { teams: resetPayload.teams }),
                ...(resetPayload.judges !== undefined && { judges: resetPayload.judges }),
                ...(resetPayload.statistics !== undefined && {
                  statistics: { ...current.statistics, ...resetPayload.statistics }
                })
              };

              // Replace the entire competition object to ensure new reference
              state.competitions[index] = updated;
              state.currentCompetition = updated;
              break;
            }
            case 'sessions': {
              // Replace the entire sessions array
              const updatedCompetitionWithSessions = {
                ...state.competitions[index],
                sessions: action.payload.data
              };

              state.competitions[index] = updatedCompetitionWithSessions;
              state.currentCompetition = updatedCompetitionWithSessions;

              if (action.payload.showToast) {
                state.toast = {
                  message: action.payload.toastMessage || 'Sessions updated successfully',
                  severity: 'success',
                  undoAction: action.payload.undoAction || null
                };
              }
              break;
            }
            case 'session':
              // console.log('=== SESSION UPDATE DEBUG ===');
              // console.log('Looking for session:', action.payload.sessionId || action.payload.sessionNumber);
              // console.log('Current sessions:', state.competitions[index].sessions.map(s => ({ id: s.id, sessionNumber: s.sessionNumber, description: s.description })));

              // Support both sessionId and sessionNumber for finding the session
              const sessionIndex = state.competitions[index].sessions
                .findIndex(s => {
                  if (action.payload.sessionId) {
                    return s.id === action.payload.sessionId;
                  }
                  return s.sessionNumber === action.payload.sessionNumber;
                });

              // console.log('Found session at index:', sessionIndex);

              if (sessionIndex !== -1) {
                // console.log('Before update:', state.competitions[index].sessions[sessionIndex]);
                // console.log('Update data:', action.payload.data);

                // Create new sessions array with updated session
                const updatedSessions = [...state.competitions[index].sessions];
                updatedSessions[sessionIndex] = {
                  ...updatedSessions[sessionIndex],
                  ...action.payload.data
                };

                // console.log('After update (session):', updatedSessions[sessionIndex]);
                // console.log('Total sessions count:', updatedSessions.length);

                // Create new competition object with updated sessions
                const updatedCompetitionWithSession = {
                  ...state.competitions[index],
                  sessions: updatedSessions
                };

                // Update both competitions array and currentCompetition
                state.competitions[index] = updatedCompetitionWithSession;
                state.currentCompetition = updatedCompetitionWithSession;

                // console.log('Final session count in state:', state.competitions[index].sessions.length);
                // console.log('=== END SESSION UPDATE DEBUG ===');

                if (action.payload.showToast) {
                  state.toast = {
                    message: `Session ${action.payload.sessionNumber} updated successfully. Saving new revision...`,
                    severity: 'success',
                    undoAction: action.payload.undoAction || null
                  };
                }
              }
              break;
            default:
              // Full competition update
              state.competitions[index] = {
                ...action.payload,
                addedAt: state.competitions[index].addedAt // Preserve original timestamp
              };
          }
        } else {
          // Full competition update
          state.competitions[index] = {
            ...action.payload,
            addedAt: state.competitions[index].addedAt // Preserve original timestamp
          };
        }
        state.currentCompetition = state.competitions[index];
      }
    },
    addDefaultRotations: (state, action) => {
      const { competitionId, showToast } = action.payload;
      const index = state.competitions.findIndex(
        comp => comp.competition.id === competitionId
      );
      
      if (index !== -1) {
        if (!state.competitions[index].rotations || 
            state.competitions[index].rotations.length === 0) {
          state.competitions[index].rotations = [DEFAULT_MENS_ROTATION, DEFAULT_WOMENS_ROTATION];
          if (state.currentCompetition?.competition.id === competitionId) {
            state.currentCompetition = state.competitions[index];
          }
          if (showToast) {
            state.toast = {
              message: 'Default rotation added successfully',
              severity: 'success',
              undoAction: action.payload.undoAction || null
            };
          }
        } else if (showToast) {
          state.toast = {
            message: 'Default rotation already exists',
            severity: 'warning',
            undoAction: action.payload.undoAction || null
          };
        }
      }
    },
    setToast: (state, action) => {
      state.toast = {
        message: action.payload.message,
        severity: action.payload.severity || 'info',
        undoAction: action.payload.undoAction || null
      };
    },
    clearToast: (state) => {
      state.toast = null;
    },
    saveVersion: (state, action) => {
      state.loading = true;
      state.error = null;
      state.toast = {
        message: 'Saving version...',
        severity: 'info',
        undoAction: action.payload?.undoAction || null
      };
    },
    saveVersionSuccess: (state, action) => {
      state.loading = false;
      state.toast = {
        message: action.payload.message || `Version ${action.payload.revision} saved successfully`,
        severity: 'success',
        undoAction: action.payload?.undoAction || null
      };
    },
    saveVersionError: (state, action) => {
      state.loading = false;
      state.error = action.payload;
      state.toast = {
        message: `Error saving version: ${action.payload}`,
        severity: 'error',
        undoAction: action.payload?.undoAction || null
      };
    },
    loadVersion: (state, action) => {
      state.loading = true;
      state.error = null;
    },
    loadVersionSuccess: (state, action) => {
      const { content, revision } = action.payload;
      state.currentCompetition = content;
      const index = state.competitions.findIndex(
        c => c.competition.id === content.competition.id
      );
      if (index !== -1) {
        state.competitions[index] = content;
      }
      state.toast = {
        message: `Version ${revision} loaded successfully`,
        severity: 'success',
        undoAction: action.payload?.undoAction || null
      };
      state.loading = false;
    },
    loadVersionError: (state, action) => {
      state.loading = false;
      state.error = action.payload;
      state.toast = {
        message: `Error loading version: ${action.payload}`,
        severity: 'error',
        undoAction: action.payload?.undoAction || null
      };
    },
    addLevelsFromSessions: (state, action) => {
      const { competitionId, showToast } = action.payload;
      const index = state.competitions.findIndex(
        comp => comp.competition.id === competitionId
      );
      
      if (index !== -1) {
        const competition = state.competitions[index];
        if (!competition.sessions?.length) {
          if (showToast) {
            state.toast = {
              message: 'No sessions found to extract levels from',
              severity: 'warning',
              undoAction: action.payload.undoAction || null
            };
          }
          return;
        }

        // Extract unique levels from sessions
        const uniqueLevels = [...new Set(
          competition.sessions.flatMap(session => 
            session.levels || []
          )
        )].map(levelId => ({
          id: levelId,
          name: `Level ${levelId}`,
          code: levelId,
          gender: 'F', // Default to female, can be updated later
          discipline: 'WAG' // Default to WAG, can be updated later
        }));

        if (uniqueLevels.length === 0) {
          if (showToast) {
            state.toast = {
              message: 'No levels found in sessions',
              severity: 'warning',
              undoAction: action.payload.undoAction || null
            };
          }
          return;
        }

        // Add levels to competition
        state.competitions[index].levels = uniqueLevels;
        
        if (state.currentCompetition?.competition.id === competitionId) {
          state.currentCompetition = state.competitions[index];
        }

        if (showToast) {
          state.toast = {
            message: `${uniqueLevels.length} levels added from sessions`,
            severity: 'success',
            undoAction: action.payload.undoAction || null
          };
        }
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCompetition.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCompetition.fulfilled, (state, action) => {
        state.loading = false;
        const competition = action.payload;
        const index = state.competitions.findIndex(
          c => c?.competition?.id === competition.competition.id
        );
        if (index !== -1) {
          state.competitions[index] = competition;
        } else {
          state.competitions.push(competition);
        }
        state.currentCompetition = competition;
      })
      .addCase(fetchCompetition.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(addCompetition.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addCompetition.fulfilled, (state, action) => {
        state.loading = false;
        state.competitions.push(action.payload);
        state.currentCompetition = action.payload;
        state.accessRights[action.payload.competition.id] = 'owner';
      })
      .addCase(addCompetition.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(duplicateCompetition.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(duplicateCompetition.fulfilled, (state, action) => {
        state.loading = false;
        state.competitions.push(action.payload);
        state.currentCompetition = action.payload;
        state.accessRights[action.payload.competition.id] = 'owner';
        state.toast = {
          message: 'Competition duplicated successfully',
          severity: 'success',
          undoAction: action.payload.undoAction || null
        };
      })
      .addCase(duplicateCompetition.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
        state.toast = {
          message: `Error duplicating competition: ${action.error.message}`,
          severity: 'error'
        };
      })
      .addCase(deleteCompetitionFromDB.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteCompetitionFromDB.fulfilled, (state, action) => {
        state.loading = false;
        // Remove the competition from the state
        state.competitions = state.competitions.filter(comp =>
          comp.competition.id !== action.payload
        );
        // Clear current competition if it was the deleted one
        if (state.currentCompetition?.competition.id === action.payload) {
          state.currentCompetition = null;
        }
        // Remove from access rights
        delete state.accessRights[action.payload];
        state.toast = {
          message: 'Competition deleted successfully',
          severity: 'success'
        };
      })
      .addCase(deleteCompetitionFromDB.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
        state.toast = {
          message: `Error deleting competition: ${action.error.message}`,
          severity: 'error'
        };
      });
  },
});

export const {
  setCompetitions,
  setCurrentCompetition,
  setLoading,
  setError,
  addCompetitionStart,
  addCompetitionSuccess,
  addCompetitionError,
  deleteCompetition,
  updateCompetition,
  addDefaultRotations,
  addLevelsFromSessions,
  setToast,
  clearToast,
  saveVersion,
  saveVersionSuccess,
  saveVersionError,
  loadVersion,
  loadVersionSuccess,
  loadVersionError,
} = competitionSlice.actions;

export default competitionSlice.reducer;
